import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SearchHistoryDto } from '@app/data/dtos/search/search-history.dto';
import { SearchModelCollectionDto } from '@app/data/dtos/search/search-model-collection.dto';
import { SearchPublicationCollectionDto } from '@app/data/dtos/search/search-publication-collection.dto';
import { SearchSectionCollectionDto } from '@app/data/dtos/search/search-section-collection.dto';
import { SearchDto } from '@app/data/dtos/search/search.dto';
import {
  mapSearch,
  mapSearchHistory,
  mapSearchHistoryRequest,
  mapSearchModelCollection,
  mapSearchParams,
  mapSearchPublicationCollection,
  mapSearchSectionCollection
} from '@app/data/mappers/search/search.mapper';
import { SEARCH_REPOSITORY, SearchRepository } from '@app/domain/repositories/search/search.repository.contract';
import { GetSearchParams } from '@app/entities/interfaces/search/get-search-params';
import { SearchHistoryRequest } from '@app/entities/models/search/search-history-request.model';
import { SearchHistory } from '@app/entities/models/search/search-history.model';
import { SearchModelCollection } from '@app/entities/models/search/search-model-collection.model';
import { SearchPublicationCollection } from '@app/entities/models/search/search-publication-collection.model';
import { SearchSectionCollection } from '@app/entities/models/search/search-section-collection.model';
import { Search } from '@app/entities/models/search/search.model';

import { SearchUseCases } from './search.use-cases.contract';

@Injectable({ providedIn: 'root' })
export class SearchUseCasesImpl implements SearchUseCases {
  #searchRepository: SearchRepository = inject(SEARCH_REPOSITORY);

  getSearch(searchParams: GetSearchParams): Observable<Search> {
    return this.#searchRepository
      .getSearch(mapSearchParams(searchParams))
      .pipe(map((search: SearchDto): Search => mapSearch(search)));
  }

  getSearchHistory(): Observable<SearchHistory> {
    return this.#searchRepository
      .getSearchHistory()
      .pipe(map((search: SearchHistoryDto): SearchHistory => mapSearchHistory(search)));
  }

  setSearchHistory(body: SearchHistoryRequest): Observable<null> {
    return this.#searchRepository.setSearchHistory(mapSearchHistoryRequest(body));
  }

  getSearchModel(searchParams: GetSearchParams): Observable<SearchModelCollection> {
    return this.#searchRepository
      .getSearchModel(mapSearchParams(searchParams))
      .pipe(map((search: SearchModelCollectionDto): SearchModelCollection => mapSearchModelCollection(search)));
  }

  getSearchPublications(searchParams: GetSearchParams): Observable<SearchPublicationCollection> {
    return this.#searchRepository
      .getSearchPublications(mapSearchParams(searchParams))
      .pipe(
        map(
          (search: SearchPublicationCollectionDto): SearchPublicationCollection =>
            mapSearchPublicationCollection(search)
        )
      );
  }

  getSearchSection(searchParams: GetSearchParams): Observable<SearchSectionCollection> {
    return this.#searchRepository
      .getSearchSection(mapSearchParams(searchParams))
      .pipe(map((search: SearchSectionCollectionDto): SearchSectionCollection => mapSearchSectionCollection(search)));
  }
}
